import { motion } from "framer-motion";
import { Target, Eye, Compass, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, SectionHead } from "../components/Reveal";
import { VALUES, COMPANY } from "../data/content";

const PILLARS = [
  {
    icon: Target,
    title: "Mission",
    text: "To help businesses, brands and creators navigate the rapidly changing digital media ecosystem through creative strategy, technology, content and measurable marketing.",
  },
  {
    icon: Eye,
    title: "Vision",
    text: "To become the most trusted media and social growth partner for ambitious brands in India and beyond — known for creativity that performs and performance that lasts.",
  },
  {
    icon: Compass,
    title: "Approach",
    text: "Strategy before spend. Creativity with a job to do. Distribution engineered for reach. Measurement on everything. We operate as an extension of your team, not a vendor.",
  },
];

export default function About() {
  return (
    <main className="pt-32" data-testid="about-page">
      <Seo
        title="About Us"
        description="Trendsight Media's mission is to help businesses, brands and creators navigate the digital media ecosystem through creative strategy, technology and measurable marketing."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-mono-accent text-xs uppercase tracking-[0.35em] text-blue-400 mb-5"
        >
          About Trendsight Media
        </motion.p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-50 leading-[1.02] max-w-4xl" data-testid="about-headline">
          {["WE BUILD DIGITAL", "PRESENCE WITH PURPOSE."].map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className={`block ${i === 1 ? "text-gradient" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.85, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          Trendsight Media Private Limited is a media-tech and social growth company from Delhi. We work with startups, SMEs, enterprises, e-commerce brands, creators and organizations that refuse to be ignored.
        </motion.p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 grid lg:grid-cols-3 gap-5">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <div className="h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-8 hover:border-blue-500/50 transition-colors duration-300" data-testid={`about-pillar-${p.title.toLowerCase()}`}>
              <p.icon size={26} className="text-blue-400" />
              <h2 className="font-display text-xl font-bold text-slate-50 mt-5">{p.title}</h2>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-[#0A0F1C] border-y border-slate-800/50 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <SectionHead eyebrow="Our Values" title={<>WHAT WE <span className="text-gradient">STAND FOR</span></>} testid="values-headline" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-6" data-testid={`value-card-${v.title.toLowerCase()}`}>
                  <CheckCircle2 size={20} className="text-blue-400" />
                  <h3 className="font-display text-base font-bold text-slate-50 mt-4">{v.title}</h3>
                  <p className="text-[13px] text-slate-400 mt-2 leading-relaxed">{v.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-24">
        <SectionHead eyebrow="Company Profile" title={<>REGISTERED & <span className="text-gradient">ACCOUNTABLE</span></>} testid="profile-headline" />
        <Reveal delay={0.15}>
          <div className="mt-12 glass-panel rounded-2xl p-8 sm:p-10 grid sm:grid-cols-2 gap-x-12 gap-y-7" data-testid="company-profile-card">
            {[
              ["Legal Name", COMPANY.name],
              ["CIN", COMPANY.cin],
              ["GSTIN", COMPANY.gstin],
              ["Phone", COMPANY.phoneDisplay],
              ["Email", COMPANY.email],
              ["Registered Address", COMPANY.address],
            ].map(([label, value]) => (
              <div key={label}>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</p>
                <p className="text-sm text-slate-200 mt-1.5 leading-relaxed">{value}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </main>
  );
}
